import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react';

interface DualLabelEdgeData {
  sourceLabel?: string;
  targetLabel?: string;
  logic?: string;
  condition?: string;
}

export function DualLabelEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
}: EdgeProps) {
  const edgeData = data as DualLabelEdgeData | undefined;
  const { sourceLabel, targetLabel, logic, condition } = edgeData ?? {};

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const hasTransition = sourceLabel && targetLabel && sourceLabel !== targetLabel;
  const singleLabel = sourceLabel || targetLabel;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{ stroke: '#d1d5db', strokeWidth: 1.5 }}
      />
      <EdgeLabelRenderer>

        {/* ── Main label pill — centered above the edge midpoint ── */}
        {singleLabel && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, calc(-100% - 10px)) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
              maxWidth: 180,
              textAlign: 'center',
            }}
            className="nodrag nopan"
          >
            <div
              style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                padding: '3px 8px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                display: 'inline-block',
                textAlign: 'left',
                lineHeight: 1.5,
              }}
            >
              {hasTransition ? (
                <>
                  {/* Source label — what leaves the box */}
                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {sourceLabel}
                  </div>
                  {/* Arrow divider */}
                  <div style={{ fontSize: '0.6rem', color: '#d1d5db', lineHeight: 1, margin: '1px 0' }}>↓</div>
                  {/* Target label — what enters the next box */}
                  <div style={{ fontSize: '0.68rem', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {targetLabel}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: '0.68rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {singleLabel}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Logic circle — ON the edge midpoint ── */}
        {logic && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
            className="nodrag nopan"
          >
            <div
              style={{
                width: 20,
                height: 20,
                background: '#fff',
                border: '2px solid #a5b4fc',
                borderRadius: '50%',
              }}
            />
            <span
              style={{
                fontSize: '0.6rem',
                color: '#6366f1',
                background: '#eff6ff',
                padding: '1px 5px',
                borderRadius: 4,
                border: '1px solid #e0e7ff',
                whiteSpace: 'nowrap',
              }}
            >
              {logic}
            </span>
          </div>
        )}

        {/* ── Condition — italic gray, below midpoint ── */}
        {condition && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, 10px) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
            }}
            className="nodrag nopan"
          >
            <span
              style={{
                fontSize: '0.6rem',
                color: '#9ca3af',
                fontStyle: 'italic',
                background: '#fff',
                padding: '1px 5px',
                borderRadius: 4,
                border: '1px solid #f3f4f6',
                whiteSpace: 'nowrap',
              }}
            >
              {condition}
            </span>
          </div>
        )}

      </EdgeLabelRenderer>
    </>
  );
}
